import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { ComboBox, FormGroup, InlineNotification, Stack } from '@carbon/react';
import { FormikDatePicker } from 'src/components/Form/FormikDatePicker';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { FormikRadioButtonGroup } from 'src/components/Form/FormikRadioButtonGroup';
import { useAllInventoryItems, useInventoryStockLevels } from 'src/hooks/useInventoryItems';
import {
  InventoryItemStatusEnum,
  InventoryMovementTypeEnum,
  InventoryMovementTypes,
  formatQuantity,
} from 'src/utils/inventory';
import { movementFormSchema } from './movementFormSchema';

interface InventoryMovementFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  presetItemId?: string | null;
}

const InventoryMovementForm = (props: InventoryMovementFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();
  const { data: items } = useAllInventoryItems();
  const { data: stockLevels } = useInventoryStockLevels();

  const itemEntities = items?.data || [];
  const activeItemOptions = itemEntities
    .filter((item) => item.status === InventoryItemStatusEnum.ACTIVE)
    .map((item) => ({ id: item.id, text: item.name }));

  useImperativeHandle(ref, () => ({
    submitForm: formikRef.current?.submitForm,
    setFieldValue: formikRef.current?.setFieldValue,
    resetForm: () =>
      formikRef.current?.resetForm({
        values: {
          itemId: props.presetItemId || '',
          type: InventoryMovementTypeEnum.RECEIVE,
          quantity: '',
          note: '',
          date: new Date(),
        },
      }),
  }));

  return (
    <div id={'inventory-movement-form'}>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        validationSchema={toFormikValidationSchema(movementFormSchema)}
        initialValues={{
          itemId: props.presetItemId || '',
          type: InventoryMovementTypeEnum.RECEIVE,
          quantity: '',
          note: '',
          date: new Date(),
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => {
          const selectedItem = itemEntities.find((item) => item.id === values.itemId);
          const stock = (stockLevels || []).find((level) => level.itemId === values.itemId)?.quantity ?? 0;
          return (
            <FormGroup legendText={''}>
              <Stack gap={5}>
                <ComboBox
                  id={'inventory-movement-item'}
                  titleText={t('common.inventoryItem')}
                  items={activeItemOptions}
                  disabled={!!props.presetItemId}
                  selectedItem={activeItemOptions.find((option) => option.id === values.itemId)}
                  onChange={(e) => setFieldValue('itemId', e.selectedItem?.id || '')}
                  itemToString={(item) => (item ? item.text : '')}
                />
                <FormikRadioButtonGroup
                  name={'type'}
                  id={'inventory-movement-type-radio'}
                  label={t('common.type')}
                  value={values.type!}
                  onChange={(value) => setFieldValue('type', value)}
                  useTranslation
                  options={InventoryMovementTypes}
                />
                <FormikTextInput name={'quantity'} isNumber allowNegative label={t('common.quantity')} />
                {values.itemId && selectedItem && (
                  <div>
                    {t('common.currentStock')}: {formatQuantity(stock, selectedItem.unit)}
                  </div>
                )}
                <FormikDatePicker
                  label={t('common.date')}
                  value={values.date}
                  onChange={(dates) => setFieldValue('date', dates[0])}
                />
                <FormikTextInput name={'note'} label={t('common.notes')} />
              </Stack>
            </FormGroup>
          );
        }}
      </Formik>
    </div>
  );
};

export default React.forwardRef(InventoryMovementForm);
